import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Grid,
  Paper,
  Alert,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import {
  createGlobalRequirement,
  updateGlobalRequirement,
  updateGlobalRequirementStatus,
  deleteGlobalRequirement,
  markGlobalRequirementForDeletion,
  listGlobalRequirements,
  GLOBAL_REQUIREMENT_SECTION,
  REQUIREMENT_PRIORITY,
  REQUIREMENT_TYPE,
  REQUIREMENT_STATUS,
  getPriorityDisplay,
  getTypeDisplay,
  getStatusDisplay,
  getSectionDisplay,
  getStatusColor,
  getPriorityColor,
} from '../../services/requirementsService';


const ProjectRequirementsSection = ({ projectId, canEdit = true }) => {
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState([GLOBAL_REQUIREMENT_SECTION.GENERAL]);
  const [editingRequirement, setEditingRequirement] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchRequirements = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listGlobalRequirements(projectId);
      setRequirements(data);
      setError(null);
    } catch (err) {
      setError('Failed to load requirements');
      console.error('Error fetching requirements:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchRequirements();
  }, [fetchRequirements]);

  const handleCreateRequirement = (section) => {
    setEditingRequirement({
      section,
      title: '',
      body: '',
      priority: REQUIREMENT_PRIORITY.SHOULD,
      req_type: REQUIREMENT_TYPE.FUNCTIONAL,
      phase_number: null,
      acceptance_criteria: [],
      non_goals: [],
      notes: '',
    });
    setDialogOpen(true);
  };

  const handleEditRequirement = (requirement) => {
    setEditingRequirement({ ...requirement });
    setDialogOpen(true);
  };

  const handleSaveRequirement = async () => {
    try {
      const requirementData = {
        section: editingRequirement.section,
        title: editingRequirement.title,
        body: editingRequirement.body,
        priority: editingRequirement.priority,
        req_type: editingRequirement.req_type,
        phase_number: editingRequirement.phase_number,
        acceptance_criteria: editingRequirement.acceptance_criteria || [],
        non_goals: editingRequirement.non_goals || [],
        notes: editingRequirement.notes,
      };

      if (editingRequirement.id) {
        // Update existing requirement
        await updateGlobalRequirement(projectId, editingRequirement.id, requirementData);
        setSnackbar({ open: true, message: 'Requirement updated successfully', severity: 'success' });
      } else {
        // Create new requirement
        await createGlobalRequirement(projectId, requirementData);
        setSnackbar({ open: true, message: 'Requirement created successfully', severity: 'success' });
      }

      setDialogOpen(false);
      setEditingRequirement(null);
      fetchRequirements();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save requirement', severity: 'error' });
      console.error('Error saving requirement:', err);
    }
  };

  const handleUpdateStatus = async (requirementId, newStatus) => {
    const prevRequirements = requirements;
    // Optimistically update local state for instant UI feedback
    setRequirements((prev) =>
      prev.map((r) => (r.id === requirementId ? { ...r, status: newStatus } : r))
    );
    try {
      await updateGlobalRequirementStatus(projectId, requirementId, newStatus);
      setSnackbar({ open: true, message: 'Status updated successfully', severity: 'success' });
    } catch (err) {
      // Revert on failure
      setRequirements(prevRequirements);
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
      console.error('Error updating status:', err);
    }
  };

  const handleDeleteRequirement = async (requirement) => {
    const isCommitted = requirement.status === REQUIREMENT_STATUS.COMMITTED;
    const confirmMsg = isCommitted
      ? 'This requirement has been committed. Marking it for deletion will remove it in the next checkpoint. Continue?'
      : 'Are you sure you want to delete this requirement?';

    if (window.confirm(confirmMsg)) {
      try {
        if (isCommitted) {
          await markGlobalRequirementForDeletion(projectId, requirement.id);
          setSnackbar({ open: true, message: 'Requirement marked for deletion and ready for checkpoint', severity: 'success' });
        } else {
          await deleteGlobalRequirement(projectId, requirement.id);
          setSnackbar({ open: true, message: 'Requirement deleted successfully', severity: 'success' });
        }
        fetchRequirements();
      } catch (err) {
        setSnackbar({ open: true, message: 'Failed to delete requirement', severity: 'error' });
        console.error('Error deleting requirement:', err);
      }
    }
  };

  const handleSectionToggle = (section) => (event, isExpanded) => {
    const newExpanded = isExpanded
      ? [...expandedSections, section]
      : expandedSections.filter(s => s !== section);
    setExpandedSections(newExpanded);
  };

  const groupRequirementsBySection = () => {
    const sections = {};
    Object.values(GLOBAL_REQUIREMENT_SECTION).forEach(section => {
      sections[section] = requirements.filter(req => req.section === section);
    });
    return sections;
  };

  const renderRequirementCard = (requirement) => (
    <Paper key={requirement.id} sx={{ p: 2, mb: 2 }}>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={8}>
          <Typography variant="h6" component="div">
            <Tooltip title="Unique identifier for this requirement — used for traceability in design documents">
              <Chip
                label={requirement.req_id}
                size="small"
                sx={{ mr: 1, backgroundColor: '#f5f5f5' }}
              />
            </Tooltip>
            {requirement.title}
          </Typography>
          {requirement.body && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              {requirement.body}
            </Typography>
          )}
          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Tooltip title="Priority: Must = required for launch · Should = important but not blocking · Could = nice to have · Won't = explicitly out of scope">
              <Chip
                label={getPriorityDisplay(requirement.priority)}
                size="small"
                sx={{ backgroundColor: getPriorityColor(requirement.priority), color: 'white' }}
              />
            </Tooltip>
            <Tooltip title="Type: Functional = what the system does · Non-Functional = how it performs (speed, security, accessibility)">
              <Chip
                label={getTypeDisplay(requirement.req_type)}
                size="small"
                variant="outlined"
              />
            </Tooltip>
            <Tooltip title="Status: Draft = in progress · Ready = queued for checkpoint · Committed = locked in a checkpoint">
              <Chip
                label={getStatusDisplay(requirement.status)}
                size="small"
                sx={{ backgroundColor: getStatusColor(requirement.status), color: 'white' }}
              />
            </Tooltip>
            {requirement.phase_number && (
              <Tooltip title="The development phase this requirement is planned for">
                <Chip
                  label={`Phase ${requirement.phase_number}`}
                  size="small"
                  variant="outlined"
                />
              </Tooltip>
            )}
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            {canEdit && (
              <>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => handleEditRequirement(requirement)}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={requirement.status === REQUIREMENT_STATUS.COMMITTED ? "Mark for Deletion" : "Delete"}>
                  <IconButton size="small" onClick={() => handleDeleteRequirement(requirement)}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {requirement.status === REQUIREMENT_STATUS.DRAFT && canEdit && (
              <Tooltip title="Queue this requirement for the next checkpoint. Once all required requirements are marked ready, go to Requirements Overview to create a checkpoint.">
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => handleUpdateStatus(requirement.id, REQUIREMENT_STATUS.READY_FOR_CHECKPOINT)}
                >
                  Mark Ready
                </Button>
              </Tooltip>
            )}
            {requirement.status === REQUIREMENT_STATUS.READY_FOR_CHECKPOINT && canEdit && (
              <Tooltip title="Remove this requirement from the checkpoint queue and return it to Draft status for further editing">
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleUpdateStatus(requirement.id, REQUIREMENT_STATUS.DRAFT)}
                >
                  Back to Draft
                </Button>
              </Tooltip>
            )}
          </Box>
        </Grid>
      </Grid>
      {requirement.acceptance_criteria && requirement.acceptance_criteria.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Acceptance Criteria:
          </Typography>
          {requirement.acceptance_criteria.map((criterion, index) => (
            <Typography key={index} variant="body2" sx={{ pl: 2, mb: 1 }}>
              • Given {criterion.given}, When {criterion.when}, Then {criterion.then}
            </Typography>
          ))}
        </Box>
      )}
      {requirement.non_goals && requirement.non_goals.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Non-Goals:
          </Typography>
          {requirement.non_goals.map((goal, index) => (
            <Typography key={index} variant="body2" sx={{ pl: 2, mb: 1 }}>
              • {goal}
            </Typography>
          ))}
        </Box>
      )}
      {requirement.notes && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Notes:
          </Typography>
          <Typography variant="body2" sx={{ pl: 2 }}>
            {requirement.notes}
          </Typography>
        </Box>
      )}
    </Paper>
  );

  const renderRequirementDialog = () => (
    <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        {editingRequirement?.id ? 'Edit Requirement' : 'Create Requirement'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={editingRequirement?.id && editingRequirement?.req_id ? 6 : 12}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Section</InputLabel>
              <Select
                value={editingRequirement?.section || GLOBAL_REQUIREMENT_SECTION.GENERAL}
                onChange={(e) => setEditingRequirement({ ...editingRequirement, section: e.target.value })}
                disabled={!!editingRequirement?.id}
              >
                {Object.values(GLOBAL_REQUIREMENT_SECTION).map(section => (
                  <MenuItem key={section} value={section}>
                    {getSectionDisplay(section)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {editingRequirement?.id && editingRequirement?.req_id && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="normal"
                label="Requirement ID"
                value={editingRequirement.req_id}
                InputProps={{ readOnly: true }}
                helperText="Auto-assigned — cannot be changed"
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              margin="normal"
              label="Title"
              value={editingRequirement?.title || ''}
              onChange={(e) => setEditingRequirement({ ...editingRequirement, title: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              margin="normal"
              label="Description"
              value={editingRequirement?.body || ''}
              onChange={(e) => setEditingRequirement({ ...editingRequirement, body: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Priority</InputLabel>
              <Select
                value={editingRequirement?.priority || REQUIREMENT_PRIORITY.SHOULD}
                onChange={(e) => setEditingRequirement({ ...editingRequirement, priority: e.target.value })}
              >
                {Object.values(REQUIREMENT_PRIORITY).map(priority => (
                  <MenuItem key={priority} value={priority}>
                    {getPriorityDisplay(priority)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Type</InputLabel>
              <Select
                value={editingRequirement?.req_type || REQUIREMENT_TYPE.FUNCTIONAL}
                onChange={(e) => setEditingRequirement({ ...editingRequirement, req_type: e.target.value })}
              >
                {Object.values(REQUIREMENT_TYPE).map(type => (
                  <MenuItem key={type} value={type}>
                    {getTypeDisplay(type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 0 }}>
                Acceptance Criteria
              </Typography>
              <Tooltip 
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: 'inherit' }}>
                      What are Acceptance Criteria?
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, color: 'inherit' }}>
                      Acceptance criteria define the conditions that must be met for a requirement to be considered complete. They follow the Given-When-Then format:
                    </Typography>
                    <Typography variant="body2" component="div" sx={{ color: 'inherit' }}>
                      <strong>Examples:</strong><br/>
                      • <strong>Given</strong> I am logged in as a user<br/>
                      &nbsp;&nbsp;<strong>When</strong> I click the logout button<br/>
                      &nbsp;&nbsp;<strong>Then</strong> I am redirected to the login page<br/><br/>
                      • <strong>Given</strong> I have items in my cart<br/>
                      &nbsp;&nbsp;<strong>When</strong> I proceed to checkout<br/>
                      &nbsp;&nbsp;<strong>Then</strong> I see the payment form
                    </Typography>
                  </Box>
                }
                arrow
                placement="top"
              >
                <IconButton size="small" sx={{ ml: 1 }}>
                  <HelpOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </IconButton>
              </Tooltip>
            </Box>
            {editingRequirement?.acceptance_criteria?.map((criterion, index) => (
              <Box key={index} sx={{ display: 'flex', mb: 2, gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Criterion {index + 1}
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Given"
                        value={criterion.given || ''}
                        onChange={(e) => {
                          const newCriteria = [...editingRequirement.acceptance_criteria];
                          newCriteria[index] = { ...criterion, given: e.target.value };
                          setEditingRequirement({ ...editingRequirement, acceptance_criteria: newCriteria });
                        }}
                        placeholder="Given the context..."
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="When"
                        value={criterion.when || ''}
                        onChange={(e) => {
                          const newCriteria = [...editingRequirement.acceptance_criteria];
                          newCriteria[index] = { ...criterion, when: e.target.value };
                          setEditingRequirement({ ...editingRequirement, acceptance_criteria: newCriteria });
                        }}
                        placeholder="When I do..."
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Then"
                          value={criterion.then || ''}
                          onChange={(e) => {
                            const newCriteria = [...editingRequirement.acceptance_criteria];
                            newCriteria[index] = { ...criterion, then: e.target.value };
                            setEditingRequirement({ ...editingRequirement, acceptance_criteria: newCriteria });
                          }}
                          placeholder="Then this should happen..."
                        />
                        {editingRequirement.acceptance_criteria.length > 1 && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              const newCriteria = editingRequirement.acceptance_criteria.filter((_, i) => i !== index);
                              setEditingRequirement({ ...editingRequirement, acceptance_criteria: newCriteria });
                            }}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                const newCriteria = [...(editingRequirement.acceptance_criteria || []), { given: '', when: '', then: '' }];
                setEditingRequirement({ ...editingRequirement, acceptance_criteria: newCriteria });
              }}
              sx={{ mt: 1 }}
            >
              Add Acceptance Criterion
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Non-Goals
            </Typography>
            {editingRequirement?.non_goals?.map((goal, index) => (
              <Box key={index} sx={{ display: 'flex', mb: 1, gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  size="small"
                  value={goal || ''}
                  onChange={(e) => {
                    const newGoals = [...editingRequirement.non_goals];
                    newGoals[index] = e.target.value;
                    setEditingRequirement({ ...editingRequirement, non_goals: newGoals });
                  }}
                  placeholder="This requirement will NOT..."
                />
                {editingRequirement.non_goals.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newGoals = editingRequirement.non_goals.filter((_, i) => i !== index);
                      setEditingRequirement({ ...editingRequirement, non_goals: newGoals });
                    }}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                const newGoals = [...(editingRequirement.non_goals || []), ''];
                setEditingRequirement({ ...editingRequirement, non_goals: newGoals });
              }}
              sx={{ mt: 1 }}
            >
              Add Non-Goal
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDialogOpen(false)} startIcon={<CancelIcon />}>
          Cancel
        </Button>
        <Button
          onClick={handleSaveRequirement}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={!editingRequirement?.title}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return <Typography>Loading requirements...</Typography>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const sections = groupRequirementsBySection();

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Project Requirements</Typography>
        {canEdit && (
          <Tooltip title="Add a new project-wide requirement. Use sections to categorize requirements by area (General, Tech Stack, Performance, Security, etc.)">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleCreateRequirement(GLOBAL_REQUIREMENT_SECTION.GENERAL)}
            >
              Add Requirement
            </Button>
          </Tooltip>
        )}
      </Box>

      {Object.entries(sections).map(([section, sectionRequirements]) => (
        <Accordion
          key={section}
          expanded={expandedSections.includes(section)}
          onChange={handleSectionToggle(section)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              {getSectionDisplay(section)} ({sectionRequirements.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {sectionRequirements.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography color="text.secondary">
                  No requirements in this section
                </Typography>
                {canEdit && (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleCreateRequirement(section)}
                    sx={{ mt: 2 }}
                  >
                    Add First Requirement
                  </Button>
                )}
              </Box>
            ) : (
              <Box>
                {sectionRequirements.map(renderRequirementCard)}
                {canEdit && (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleCreateRequirement(section)}
                    sx={{ mt: 2 }}
                  >
                    Add Requirement
                  </Button>
                )}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      ))}

      {renderRequirementDialog()}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectRequirementsSection;
